/**
 * Enemy-ability rules the AI asks about: key/affix gating, HP windows (with affix overrides),
 * once-per-engage abilities, cast chains, shield-gated interruptibility and locked facing.
 * Chain / once bookkeeping lives in `unit.ai.timers` under reserved '@' keys (serializable,
 * cleared by evade like every other timer).
 */
import type { EnemyAbility, SimContext, Unit } from '../../contracts';
import { gatePasses, hasAffix, hasAuraOn, mstate, resolveKeyScaled } from './state';

export const onceKey = (id: string): string => `@once:${id}`;
export const chainKey = (id: string): string => `@chain:${id}`;
export const chainNextKey = (id: string): string => `@chainNext:${id}`;

/** HP window in force (an active affix may replace it). */
export function hpWindowFor(ctx: SimContext, def: EnemyAbility): { min?: number; max?: number } | undefined {
  if (def.hpWindowAffix) {
    for (const a of ctx.world.mplus?.affixes ?? []) {
      const w = def.hpWindowAffix[a];
      if (w && hasAffix(ctx, a)) return w;
    }
  }
  return def.hpWindow;
}

/** May the AI use this ability now (key/affix gates, HP window, once)? Pure. */
export function abilityUsable(ctx: SimContext, npc: Unit, def: EnemyAbility, hpFrac = npc.maxHp > 0 ? npc.hp / npc.maxHp : 0): boolean {
  const w = hpWindowFor(ctx, def);
  if (w && ((w.min !== undefined && hpFrac < w.min) || (w.max !== undefined && hpFrac > w.max))) return false;
  if (!gatePasses(ctx, { minKey: def.minKeyLevel, maxKey: def.maxKeyLevel, affix: def.requiresAffix })) return false;
  if (def.once && npc.ai?.timers[onceKey(def.id)] !== undefined) return false;
  return true;
}

/**
 * The AI just started `def` (tryCast succeeded) and set its repeat timer. Applies once/chain
 * rules on top and records a facing lock. Call after the AI's own timer update.
 */
export function onAbilityUsed(ctx: SimContext, npc: Unit, def: EnemyAbility): void {
  const ai = npc.ai;
  if (!ai) return;
  if (def.lockFacing && npc.cast?.abilityId === def.id) {
    const st = mstate(ctx.world);
    st.facingLocks = st.facingLocks.filter((l) => l.unitId !== npc.id);
    st.facingLocks.push({ unitId: npc.id, castStartedAt: npc.cast.startedAt, facing: npc.facing });
  }
  const chain = def.timer.chain;
  if (chain) {
    const castEnd = npc.cast?.abilityId === def.id ? npc.cast.endsAt : ctx.now;
    const ck = chainKey(def.id);
    const nk = chainNextKey(def.id);
    const left = ai.timers[ck];
    if (left === undefined) {
      const total = Math.max(1, Math.round(resolveKeyScaled(ctx, chain.count, 1)));
      if (total > 1) {
        ai.timers[ck] = total - 1;
        // Remember when the chain's normal repeat is due (measured from the chain's first cast).
        ai.timers[nk] = ai.timers[def.id] ?? ctx.now + def.timer.every;
        ai.timers[def.id] = castEnd + chain.gapMs;
      }
    } else if (left > 1) {
      ai.timers[ck] = left - 1;
      ai.timers[def.id] = castEnd + chain.gapMs;
    } else {
      const next = ai.timers[nk];
      delete ai.timers[ck];
      delete ai.timers[nk];
      if (def.timer.every > 0 && next !== undefined) ai.timers[def.id] = Math.max(next, castEnd + chain.gapMs);
    }
  }
  if (def.once && ai.timers[chainKey(def.id)] === undefined) {
    ai.timers[onceKey(def.id)] = ctx.now;
    delete ai.timers[def.id];
  }
}

/** Timers were (re)scheduled for a new phase: drop half-finished chains. */
export function onTimersScheduled(ctx: SimContext, npc: Unit): void {
  const t = npc.ai?.timers;
  if (!t) return;
  for (const k of Object.keys(t)) if (k.startsWith('@chain')) delete t[k];
  // Phase-specific first casts (Phase.firstMs), e.g. a breather after an intermission.
  const enc = ctx.world.encounter;
  if (!enc || !enc.bossIds.includes(npc.id)) return;
  const first = ctx.content.encounter(enc.encounterId)?.phases[npc.ai!.phaseIndex ?? enc.phaseIndex]?.firstMs;
  if (!first) return;
  for (const [id, ms] of Object.entries(first)) if (t[id] !== undefined) t[id] = ctx.now + ms;
}

/**
 * Per tick: shield-gated interruptibility (`interruptibleWhen`) and locked facing for running
 * casts. Cheap: one pass over casting NPCs.
 */
export function updateCastRules(ctx: SimContext): void {
  const w = ctx.world;
  const st = w.mechanics;
  for (const id of w.unitOrder) {
    const u = w.units[id];
    if (!u?.cast || u.kind !== 'npc' || !u.alive) continue;
    const def = ctx.content.enemyAbility(u.cast.abilityId);
    if (def?.interruptibleWhen) u.cast.interruptible = (def.interruptible ?? false) && !hasAuraOn(u, def.interruptibleWhen.casterLacksAura);
  }
  if (!st || st.facingLocks.length === 0) return;
  st.facingLocks = st.facingLocks.filter((l) => {
    const u = w.units[l.unitId];
    if (!u?.alive || !u.cast || u.cast.startedAt !== l.castStartedAt) return false;
    u.facing = l.facing;
    return true;
  });
}
